/**
 * THE ENTITY CHAT'S ONE MODEL CALL — through the live `studioChat` proxy (spec Q33), which the page passes
 * in as `post` (`SpotlightConversationService.postHop`). Spotlight's `parseInstruction` is the pattern:
 * ONE CALL, ONE RETRY, NEVER A THROW. Any failure — no tool call twice, unparseable arguments, a failed
 * request — returns null, and the caller treats it as a failed call: nothing is applied (spec, section 4).
 */
import { ENTITY_TOOL } from './entity-tool';
import { buildEntitySystem } from './entity-prompt';

export type EntityPost = (
  body: unknown,
  signal?: AbortSignal,
) => Promise<{ toolCalls?: { id: string; tool: string; args: unknown }[] }>;

export interface EntityParseInput {
  sentence: string;
  /** `buildContextBlock(...)` for this send. */
  context: string;
  post: EntityPost;
  clientId: string;
  conversationId?: string;
  signal?: AbortSignal;
  /** Earlier turns of THIS request (a question the model asked, and the sentence before it). */
  history?: { role: 'user' | 'assistant'; content: string }[];
}

/** The tool's raw arguments, or null when the call produced nothing usable. Validate before use. */
export async function callEntityParse(input: EntityParseInput): Promise<unknown | null> {
  // Was (2026-10-03, fix round 1 — FIX A, studioChat is stateless per hop, no history was carried):
  // const messages = [
  //   { role: 'system' as const, content: buildEntitySystem() },
  //   { role: 'system' as const, content: input.context },
  //   { role: 'user' as const, content: input.sentence },
  // ];
  const messages = [
    { role: 'system' as const, content: buildEntitySystem() },
    { role: 'system' as const, content: input.context },
    ...(input.history ?? []).map((h) => ({ role: h.role, content: h.content })),
    { role: 'user' as const, content: input.sentence },
  ];
  const body = {
    clientId: input.clientId,
    conversationId: input.conversationId,
    messages,
    tools: [ENTITY_TOOL],
    chat: 'entity' as const,
    outcomesOnly: false,
    retry: false,
  };
  try {
    let res = await input.post(body, input.signal);
    // `retry: true` makes studioChat send tool_choice 'required'.
    if (!res.toolCalls?.length) res = await input.post({ ...body, retry: true }, input.signal);
    // Was (2026-10-03, final review — I4, read only the first tool call):
    // const args = res.toolCalls?.[0]?.args;
    // if (!args || typeof args !== 'object') return null;
    // if ('__unparseable' in args) return null; // studioChat's safeParse could not read the JSON
    // return args;
    // ⚠ v4 models can answer with SEVERAL tool calls (studio-chat.service.ts): read them all, never just [0].
    const calls = (res.toolCalls ?? [])
      .filter((c) => c.tool === 'entity_request' && !!c.args && typeof c.args === 'object' && !('__unparseable' in (c.args as object)))
      .map((c) => c.args as Record<string, unknown>);
    if (!calls.length) return null;
    const head = calls[0];
    if (calls.length === 1 || head['kind'] !== 'edits') return head;
    const list = (a: Record<string, unknown>, k: string): unknown[] => (Array.isArray(a[k]) ? (a[k] as unknown[]) : []);
    return {
      ...head,
      edits: calls.filter((a) => a['kind'] === 'edits').flatMap((a) => list(a, 'edits')),
      gaps: calls.flatMap((a) => list(a, 'gaps')),
    };
  } catch {
    return null;
  }
}
